import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Typography,
  Box,
  Chip,
  Alert,
  AlertTitle,
  InputAdornment,
  useMediaQuery,
  useTheme,
  Avatar,
  Skeleton,
  Tooltip,
  Fade,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Menu,
  MenuItem,
  Snackbar,
  Fab,
  CircularProgress,
  Link,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Close as CloseIcon,
  Warning as WarningIcon,
  WarningAmber as WarningAmberIcon,
  CheckCircle as CheckCircleIcon,
  TrendingUp as TrendingUpIcon,
  Build as BuildIcon,
  Description as DescriptionIcon,
  MoreVert as MoreVertIcon,
  Refresh as RefreshIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { clientiService } from '../services/api';
import { iniziali, plurale } from '../utils/formato';

// Campi realmente editabili: il form NON deve trascinarsi dietro id/created_at/
// numero_cantieri, che arrivano dal GET ma non appartengono al payload di salvataggio.
const EMPTY_FORM = {
  ragione_sociale: '',
  partita_iva: '',
  codice_fiscale: '',
  indirizzo: '',
  citta: '',
  cap: '',
  provincia: '',
  telefono: '',
  email: '',
  pec: '',
  referente: '',
  telefono_referente: '',
  email_referente: '',
  note: '',
};

// Il backend legge data['ragione_sociale'] & co. senza .get(): se mancano va in
// KeyError -> 500. Li validiamo qui prima di partire con la chiamata.
const CAMPI_OBBLIGATORI = {
  ragione_sociale: 'La ragione sociale è obbligatoria',
  indirizzo: "L'indirizzo è obbligatorio",
  citta: 'La città è obbligatoria',
  cap: 'Il CAP è obbligatorio',
  provincia: 'La provincia è obbligatoria',
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Prende solo i campi del form, normalizzando i null del backend a '' (altrimenti
 *  i TextField passano da uncontrolled a controlled e React logga un warning). */
const toFormData = (cliente) =>
  Object.keys(EMPTY_FORM).reduce(
    (acc, k) => ({ ...acc, [k]: cliente?.[k] ?? '' }),
    {}
  );

const validaCliente = (form) => {
  const errori = {};

  Object.entries(CAMPI_OBBLIGATORI).forEach(([campo, messaggio]) => {
    if (!String(form[campo] || '').trim()) errori[campo] = messaggio;
  });

  if (form.cap && !/^\d{5}$/.test(form.cap.trim())) {
    errori.cap = 'Il CAP deve essere di 5 cifre';
  }
  if (form.provincia && !/^[A-Za-z]{2}$/.test(form.provincia.trim())) {
    errori.provincia = 'La provincia è la sigla di 2 lettere (es. NO)';
  }
  if (form.partita_iva && !/^\d{11}$/.test(form.partita_iva.replace(/\s/g, ''))) {
    errori.partita_iva = 'La partita IVA è di 11 cifre';
  }
  [
    ['email', "L'email non è valida"],
    ['pec', 'La PEC non è valida'],
    ['email_referente', "L'email del referente non è valida"],
  ].forEach(([campo, messaggio]) => {
    if (form[campo] && !EMAIL_RE.test(form[campo].trim())) errori[campo] = messaggio;
  });

  return errori;
};

// Il tema colora l'header di tabella in rosso pieno: senza questo il TableSortLabel
// userebbe i suoi colori di default (grigio scuro) e su rosso sparirebbe.
const SORT_LABEL_SX = {
  color: 'inherit',
  '&:hover, &:focus': { color: 'inherit' },
  '&.Mui-active': { color: 'inherit' },
  '&.Mui-active .MuiTableSortLabel-icon': { color: 'inherit', opacity: 1 },
  // la freccia compare al passaggio del mouse: se fosse sempre visibile sembrerebbe
  // che tutte le colonne siano ordinate contemporaneamente
  '& .MuiTableSortLabel-icon': { color: 'inherit', opacity: 0 },
  '&:hover .MuiTableSortLabel-icon': { opacity: 0.5 },
};

const COLONNE_ORDINABILI = {
  ragione_sociale: (c) => (c.ragione_sociale || '').toLowerCase(),
  citta: (c) => (c.citta || '').toLowerCase(),
  numero_cantieri: (c) => c.numero_cantieri || 0,
  numero_interventi: (c) => c.numero_interventi || 0,
};

// ---------------------------------------------------------------- sotto-componenti

const StatBox = ({ icon: Icon, value, label, color, compact }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: compact ? 1 : 2,
      p: compact ? 1 : 2,
      bgcolor: 'rgba(255,255,255,0.1)',
      borderRadius: 2,
      flex: 1,
      minWidth: 0,
    }}
  >
    <Box
      sx={{
        width: compact ? 28 : 44,
        height: compact ? 28 : 44,
        borderRadius: 1.5,
        bgcolor: color || 'rgba(255,255,255,0.2)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <Icon sx={{ color: 'white', fontSize: compact ? 16 : 22 }} />
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography
        sx={{
          color: 'white',
          fontWeight: 800,
          lineHeight: 1.1,
          fontSize: compact ? '1.1rem' : '1.5rem',
        }}
      >
        {value}
      </Typography>
      <Typography
        variant="caption"
        noWrap
        sx={{
          // 0.7 di opacità su fondo scuro sta sotto il rapporto 4.5:1 per il testo
          // piccolo: alziamo a 0.85 per restare leggibili anche in pieno sole.
          color: 'rgba(255,255,255,0.85)',
          display: 'block',
          // a 390px i tre riquadri hanno ~114px l'uno: senza questo "Interventi"
          // veniva troncato in "Interv…"
          fontSize: compact ? '0.68rem' : undefined,
          letterSpacing: compact ? 0 : undefined,
        }}
      >
        {label}
      </Typography>
    </Box>
  </Box>
);

const EmptyState = ({ searchTerm, onReset }) => (
  <Box sx={{ textAlign: 'center', py: { xs: 6, sm: 8 }, px: 3 }}>
    <Box
      sx={{
        width: 80,
        height: 80,
        borderRadius: '50%',
        bgcolor: '#f3f4f6',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mx: 'auto',
        mb: 2,
      }}
    >
      <BusinessIcon sx={{ fontSize: 40, color: '#9ca3af' }} />
    </Box>
    <Typography variant="h6" fontWeight={600} color="text.secondary">
      Nessun cliente trovato
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: searchTerm ? 2 : 0 }}>
      {searchTerm
        ? `Nessun risultato per "${searchTerm}"`
        : 'Inizia aggiungendo il tuo primo cliente'}
    </Typography>
    {searchTerm && (
      <Button onClick={onReset} size="small" sx={{ textTransform: 'none', fontWeight: 600 }}>
        Azzera la ricerca
      </Button>
    )}
  </Box>
);

/** Scheda cliente: usata su telefono (1 colonna) e tablet verticale (2 colonne).
 *  Tutta la scheda è il bersaglio per aprire il dettaglio; modifica ed elimina
 *  stanno in un menu, così l'azione distruttiva non è adiacente alle altre. */
const ClienteCard = ({ cliente, onApri, onModifica, onElimina }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const apriMenu = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };
  const chiudiMenu = (e) => {
    e?.stopPropagation();
    setAnchorEl(null);
  };
  const scegli = (e, azione) => {
    e.stopPropagation();
    setAnchorEl(null);
    azione(cliente);
  };

  return (
    <Paper
      elevation={0}
      onClick={() => onApri(cliente)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onApri(cliente);
        }
      }}
      aria-label={`Apri la scheda di ${cliente.ragione_sociale}`}
      sx={{
        p: 2,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        cursor: 'pointer',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'border-color .2s, box-shadow .2s',
        '&:hover': { borderColor: '#e50000', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' },
        '&:focus-visible': { outline: '2px solid #e50000', outlineOffset: 2 },
        '&:active': { bgcolor: '#fafafa' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
        <Avatar
          sx={{ width: 44, height: 44, bgcolor: '#1a1a1a', fontWeight: 700, fontSize: '0.85rem' }}
        >
          {iniziali(cliente.ragione_sociale)}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.3 }}>
            {cliente.ragione_sociale}
          </Typography>
          {cliente.partita_iva && (
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
              {cliente.partita_iva}
            </Typography>
          )}
        </Box>
        <IconButton
          onClick={apriMenu}
          aria-label={`Azioni per ${cliente.ragione_sociale}`}
          // niente margine destro negativo: faceva sporgere il pulsante di 4px oltre
          // la scheda e contribuiva allo scroll orizzontale
          sx={{ width: 44, height: 44, mt: -0.5, flexShrink: 0, color: '#6b7280' }}
        >
          <MoreVertIcon />
        </IconButton>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={chiudiMenu}>
          <MenuItem onClick={(e) => scegli(e, onApri)} sx={{ py: 1.25 }}>
            <ListItemIcon><VisibilityIcon fontSize="small" sx={{ color: '#3b82f6' }} /></ListItemIcon>
            Apri scheda
          </MenuItem>
          <MenuItem onClick={(e) => scegli(e, onModifica)} sx={{ py: 1.25 }}>
            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
            Modifica
          </MenuItem>
          <Divider />
          <MenuItem onClick={(e) => scegli(e, onElimina)} sx={{ py: 1.25, color: '#dc2626' }}>
            <ListItemIcon><DeleteIcon fontSize="small" sx={{ color: '#dc2626' }} /></ListItemIcon>
            Elimina
          </MenuItem>
        </Menu>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 1.5, flex: 1 }}>
        {(cliente.citta || cliente.provincia) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <LocationIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
            <Typography variant="body2" color="text.secondary" noWrap>
              {cliente.citta}
              {cliente.provincia ? ` (${cliente.provincia})` : ''}
            </Typography>
          </Box>
        )}
        {cliente.telefono && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
            {/* su telefono il numero deve essere chiamabile con un tap */}
            <Link
              href={`tel:${cliente.telefono}`}
              onClick={(e) => e.stopPropagation()}
              variant="body2"
              underline="hover"
              // colore da collegamento: prima erano grigi come il testo normale,
              // quindi non si capiva che erano tappabili (e si toccavano per sbaglio
              // credendo di aprire la scheda). Il padding allarga l'area del tocco.
              // inline-flex + py: su un elemento inline il padding non entra nel
              // box, quindi l'area di tocco restava di 28px anche col padding.
              sx={{
                color: 'primary.main',
                fontWeight: 600,
                display: 'inline-flex',
                alignItems: 'center',
                minHeight: 44,
                py: 0.5,
                minWidth: 0,
              }}
            >
              {cliente.telefono}
            </Link>
          </Box>
        )}
        {cliente.email && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <EmailIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
            <Link
              href={`mailto:${cliente.email}`}
              onClick={(e) => e.stopPropagation()}
              variant="body2"
              underline="hover"
              noWrap
              title={cliente.email}
              // inline-flex + py: su un elemento inline il padding non entra nel
              // box, quindi l'area di tocco restava di 28px anche col padding.
              sx={{
                color: 'primary.main',
                fontWeight: 600,
                display: 'inline-flex',
                alignItems: 'center',
                minHeight: 44,
                py: 0.5,
                minWidth: 0,
              }}
            >
              {cliente.email}
            </Link>
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Chip
          label={plurale(cliente.numero_cantieri || 0, 'cantiere', 'cantieri')}
          size="small"
          sx={{
            bgcolor: cliente.numero_cantieri > 0 ? '#dbeafe' : '#f3f4f6',
            color: cliente.numero_cantieri > 0 ? '#1e40af' : '#6b7280',
            fontWeight: 600,
            fontSize: '0.7rem',
          }}
        />
        <Chip
          label={plurale(cliente.numero_interventi || 0, 'intervento', 'interventi')}
          size="small"
          sx={{
            bgcolor: cliente.numero_interventi > 0 ? '#dcfce7' : '#f3f4f6',
            color: cliente.numero_interventi > 0 ? '#166534' : '#6b7280',
            fontWeight: 600,
            fontSize: '0.7rem',
          }}
        />
        <ChevronRightIcon sx={{ ml: 'auto', color: '#d1d5db' }} />
      </Box>
    </Paper>
  );
};

// ---------------------------------------------------------------- pagina

const Clienti = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  // Tre viste vere, non due: telefono (schede), tablet (schede a 2 colonne fino a
  // 900px, poi tabella ridotta), desktop (tabella completa).
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));        // < 600
  const isCardView = useMediaQuery(theme.breakpoints.down('md'));     // < 900
  const isCompactTable = useMediaQuery(theme.breakpoints.down('lg')); // < 1200

  const [clienti, setClienti] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [ricerca, setRicerca] = useState(''); // versione "ritardata" usata per filtrare
  const [orderBy, setOrderBy] = useState('ragione_sociale');
  const [order, setOrder] = useState('asc');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentCliente, setCurrentCliente] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [clienteToDelete, setClienteToDelete] = useState(null);
  const [deleteError, setDeleteError] = useState('');
  const [deleting, setDeleting] = useState(false);

  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [blockInfo, setBlockInfo] = useState(null);
  const [blockedCliente, setBlockedCliente] = useState(null);
  const [blockError, setBlockError] = useState('');

  const [toast, setToast] = useState(null); // { messaggio, severity }

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const primoCampoRef = useRef(null);

  const loadClienti = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError('');
      const response = await clientiService.getAll();
      setClienti(response.data);
    } catch (e) {
      // Prima questo errore finiva in `error`, che è mostrato SOLO dentro il dialog:
      // in pagina restava "Nessun cliente trovato", messaggio falso.
      setLoadError(
        e.isOffline
          ? 'Sei offline: l’elenco clienti non è aggiornato.'
          : 'Non è stato possibile caricare i clienti.'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadClienti();
  }, [loadClienti]);

  // Ricerca ritardata: evita di rifiltrare e ri-renderizzare a ogni tasto premuto.
  useEffect(() => {
    const t = setTimeout(() => setRicerca(searchTerm.trim().toLowerCase()), 200);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    setPage(0);
  }, [ricerca, orderBy, order]);

  // Derivato, non duplicato in stato: prima `filteredClienti` era un secondo useState
  // tenuto in sincronia a mano da un useEffect.
  const clientiFiltrati = useMemo(() => {
    const base = !ricerca
      ? clienti
      : clienti.filter((c) =>
          [c.ragione_sociale, c.partita_iva, c.citta, c.provincia, c.referente, c.email]
            .some((v) => String(v || '').toLowerCase().includes(ricerca))
        );

    const chiave = COLONNE_ORDINABILI[orderBy] || COLONNE_ORDINABILI.ragione_sociale;
    return [...base].sort((a, b) => {
      const va = chiave(a);
      const vb = chiave(b);
      if (va < vb) return order === 'asc' ? -1 : 1;
      if (va > vb) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }, [clienti, ricerca, orderBy, order]);

  // Se elimini gli ultimi elementi di una pagina alta, non restare su una pagina vuota.
  const ultimaPagina = Math.max(0, Math.ceil(clientiFiltrati.length / rowsPerPage) - 1);
  const paginaCorrente = Math.min(page, ultimaPagina);
  const clientiPaginati = useMemo(
    () =>
      clientiFiltrati.slice(
        paginaCorrente * rowsPerPage,
        paginaCorrente * rowsPerPage + rowsPerPage
      ),
    [clientiFiltrati, paginaCorrente, rowsPerPage]
  );

  const handleSort = (campo) => {
    if (orderBy === campo) {
      setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setOrderBy(campo);
      setOrder('asc');
    }
  };

  const totalCantieri = useMemo(
    () => clienti.reduce((acc, c) => acc + (c.numero_cantieri || 0), 0),
    [clienti]
  );
  const totalInterventi = useMemo(
    () => clienti.reduce((acc, c) => acc + (c.numero_interventi || 0), 0),
    [clienti]
  );

  const handleOpenDialog = (cliente = null) => {
    setCurrentCliente(cliente);
    setFormData(cliente ? toFormData(cliente) : EMPTY_FORM);
    setFormErrors({});
    setError('');
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    if (saving) return;
    setDialogOpen(false);
    setCurrentCliente(null);
    setFormErrors({});
    setError('');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // L'errore sparisce mentre correggi, non solo al successivo invio.
    setFormErrors((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (saving) return; // niente doppio salvataggio col doppio click

    const errori = validaCliente(formData);
    if (Object.keys(errori).length > 0) {
      setFormErrors(errori);
      const primo = document.querySelector(`[name="${Object.keys(errori)[0]}"]`);
      primo?.focus();
      primo?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      return;
    }

    const payload = {
      ...formData,
      provincia: formData.provincia.trim().toUpperCase(),
      cap: formData.cap.trim(),
      partita_iva: formData.partita_iva.replace(/\s/g, ''),
    };

    try {
      setSaving(true);
      setError('');
      if (currentCliente) {
        await clientiService.update(currentCliente.id, payload);
      } else {
        await clientiService.create(payload);
      }
      setDialogOpen(false);
      setCurrentCliente(null);
      setToast({
        messaggio: currentCliente
          ? `"${payload.ragione_sociale}" aggiornato`
          : `"${payload.ragione_sociale}" creato`,
        severity: 'success',
      });
      loadClienti();
    } catch (err) {
      if (err.isOffline && err.queued) {
        setDialogOpen(false);
        setCurrentCliente(null);
        return; // il toast globale di SyncManager avvisa già
      }
      setError(err.isOffline ? err.message : err.response?.data?.message || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDetailDialog = useCallback(
    (cliente) => navigate(`/clienti/${cliente.id}`),
    [navigate]
  );

  const handleOpenDeleteDialog = useCallback((cliente) => {
    setClienteToDelete(cliente);
    setDeleteError('');
    setDeleteDialogOpen(true);
  }, []);

  const handleCloseDeleteDialog = () => {
    if (deleting) return;
    setDeleteDialogOpen(false);
    setClienteToDelete(null);
    setDeleteError('');
  };

  const handleCloseBlockDialog = () => {
    if (deleting) return;
    setBlockDialogOpen(false);
    setBlockInfo(null);
    setBlockedCliente(null);
    setBlockError('');
  };

  const handleDelete = async () => {
    if (!clienteToDelete) return;
    const nome = clienteToDelete.ragione_sociale;
    try {
      setDeleting(true);
      setDeleteError('');
      await clientiService.delete(clienteToDelete.id);
      setDeleteDialogOpen(false);
      setClienteToDelete(null);
      setToast({ messaggio: `"${nome}" eliminato`, severity: 'success' });
      loadClienti();
    } catch (err) {
      // 409: il cliente ha cantieri/interventi collegati -> modale di cascata
      if (err.response?.status === 409 && err.response?.data?.blocco) {
        setBlockInfo(err.response.data.blocco);
        setBlockedCliente(clienteToDelete);
        setDeleteDialogOpen(false);
        setClienteToDelete(null);
        setBlockDialogOpen(true);
      } else {
        setDeleteError(
          err.response?.data?.message ||
            "Si è verificato un errore durante l'eliminazione del cliente. Riprova."
        );
      }
    } finally {
      setDeleting(false);
    }
  };

  const handleCascadeDelete = async () => {
    if (!blockedCliente) return;
    const nome = blockedCliente.ragione_sociale;
    try {
      setDeleting(true);
      setBlockError('');
      await clientiService.delete(blockedCliente.id, { cascade: true });
      setBlockDialogOpen(false);
      setBlockInfo(null);
      setBlockedCliente(null);
      setToast({ messaggio: `"${nome}" e i collegati eliminati`, severity: 'success' });
      loadClienti();
    } catch (err) {
      setBlockError(
        err.response?.data?.message ||
          "Si è verificato un errore durante l'eliminazione. Riprova."
      );
    } finally {
      setDeleting(false);
    }
  };

  // ------------------------------------------------------------- loading

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa' }}>
        <Box sx={{ bgcolor: '#1a1a1a', pt: { xs: 3, sm: 4 }, pb: { xs: 9, sm: 14 }, px: { xs: 2, sm: 3 } }}>
          <Container maxWidth="xl">
            <Skeleton variant="text" width={250} height={40} sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
            <Skeleton variant="text" width={300} height={24} sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
          </Container>
        </Box>
        <Container maxWidth="xl" sx={{ mt: { xs: -6, sm: -10 }, pb: 4 }}>
          <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <Skeleton variant="rounded" height={48} sx={{ mb: 3, borderRadius: 2, maxWidth: 500 }} />
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Skeleton key={i} variant="rounded" height={isCardView ? 150 : 64} sx={{ mb: 1.5, borderRadius: 2 }} />
            ))}
          </Paper>
        </Container>
      </Box>
    );
  }

  const vuoto = clientiFiltrati.length === 0;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa', pb: isPhone ? 10 : 0 }}>
      {/* ---------------------------------------------------------- header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
          pt: { xs: 2.5, sm: 4 },
          pb: { xs: 8, sm: 14 },
          px: { xs: 2, sm: 3 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            opacity: 0.03,
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            top: -100,
            right: -100,
            width: 300,
            height: 300,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(229,0,0,0.3) 0%, transparent 70%)',
          }}
        />

        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
              mb: { xs: 2, sm: 4 },
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="h4"
                sx={{
                  color: 'white',
                  fontWeight: 800,
                  fontSize: { xs: '1.375rem', sm: '1.75rem', md: '2rem' },
                  mb: 0.25,
                }}
              >
                Gestione Clienti
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                {clienti.length} {clienti.length === 1 ? 'cliente' : 'clienti'} in anagrafica
              </Typography>
            </Box>

            {/* Su telefono il pulsante vive nel FAB in basso, sempre raggiungibile */}
            {!isPhone && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
                sx={{
                  bgcolor: '#e50000',
                  color: 'white',
                  px: 3,
                  py: 1.25,
                  borderRadius: 2,
                  fontWeight: 600,
                  textTransform: 'none',
                  flexShrink: 0,
                  boxShadow: '0 4px 14px rgba(229,0,0,0.4)',
                  '&:hover': { bgcolor: '#c50000', boxShadow: '0 6px 20px rgba(229,0,0,0.5)' },
                }}
              >
                Nuovo Cliente
              </Button>
            )}
          </Box>

          {/* Su telefono i tre riquadri erano impilati a piena larghezza: ~200px di
              altezza prima del primo cliente. In riga restano leggibili e occupano 60px. */}
          <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 } }}>
            <StatBox icon={BusinessIcon} value={clienti.length} label="Clienti" color="rgba(229,0,0,0.3)" compact={isPhone} />
            <StatBox icon={BuildIcon} value={totalCantieri} label="Cantieri" color="rgba(59,130,246,0.3)" compact={isPhone} />
            <StatBox icon={TrendingUpIcon} value={totalInterventi} label="Interventi" color="rgba(16,185,129,0.3)" compact={isPhone} />
          </Box>
        </Container>
      </Box>

      {/* ---------------------------------------------------------- contenuto */}
      <Container maxWidth="xl" sx={{ mt: { xs: -5, sm: -10 }, pb: 4, position: 'relative', zIndex: 2 }}>
        <Paper
          elevation={0}
          sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}
        >
          {/* ricerca */}
          <Box
            sx={{
              p: { xs: 1.5, sm: 3 },
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor: '#fafafa',
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <TextField
              fullWidth
              size={isPhone ? 'small' : 'medium'}
              variant="outlined"
              placeholder={isPhone ? 'Cerca cliente…' : 'Cerca per nome, P.IVA, città, referente…'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              inputProps={{ 'aria-label': 'Cerca tra i clienti', enterKeyHint: 'search' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
                endAdornment: searchTerm ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearchTerm('')}
                      aria-label="Azzera la ricerca"
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{
                maxWidth: { md: 500 },
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'white',
                  borderRadius: 2,
                  '& fieldset': { borderColor: '#e5e7eb' },
                  '&:hover fieldset': { borderColor: '#d1d5db' },
                  '&.Mui-focused fieldset': { borderColor: '#e50000', borderWidth: 1 },
                },
              }}
            />
            {!isPhone && ricerca && (
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                {plurale(clientiFiltrati.length, 'risultato', 'risultati')}
              </Typography>
            )}
          </Box>

          {loadError && (
            <Alert
              severity="error"
              sx={{ borderRadius: 0 }}
              action={
                <Button color="inherit" size="small" startIcon={<RefreshIcon />} onClick={loadClienti}>
                  Riprova
                </Button>
              }
            >
              {loadError}
            </Alert>
          )}

          {vuoto ? (
            <EmptyState searchTerm={searchTerm} onReset={() => setSearchTerm('')} />
          ) : isCardView ? (
            /* ---- telefono (1 col) e tablet verticale (2 col) ---- */
            <Box
              sx={{
                p: { xs: 1.5, sm: 2 },
                display: 'grid',
                gap: { xs: 1.5, sm: 2 },
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
              }}
            >
              {clientiPaginati.map((cliente) => (
                <ClienteCard
                  key={cliente.id}
                  cliente={cliente}
                  onApri={handleOpenDetailDialog}
                  onModifica={handleOpenDialog}
                  onElimina={handleOpenDeleteDialog}
                />
              ))}
            </Box>
          ) : (
            /* ---- tabella: ridotta sotto i 1200px, completa sopra ---- */
            <TableContainer>
              {/* tableLayout fixed: senza, il browser ignora le larghezze qui sotto e
                  distribuisce le colonne sul contenuto, strizzando "Cliente" */}
              <Table size={isCompactTable ? 'small' : 'medium'} sx={{ tableLayout: 'fixed' }}>
                <TableHead>
                  {/* Larghezze esplicite: lasciate ad "auto" la colonna Cliente si
                      stringeva e i nomi lunghi andavano su 4 righe, mentre Contatti
                      (spesso vuota) si prendeva lo spazio. */}
                  <TableRow>
                    <TableCell sx={{ width: isCompactTable ? '34%' : '24%' }}>
                      <TableSortLabel
                        active={orderBy === 'ragione_sociale'}
                        direction={orderBy === 'ragione_sociale' ? order : 'asc'}
                        onClick={() => handleSort('ragione_sociale')}
                        sx={SORT_LABEL_SX}
                      >
                        Cliente
                      </TableSortLabel>
                    </TableCell>
                    {!isCompactTable && <TableCell sx={{ width: '12%' }}>P.IVA</TableCell>}
                    <TableCell sx={{ width: isCompactTable ? '18%' : '14%' }}>
                      <TableSortLabel
                        active={orderBy === 'citta'}
                        direction={orderBy === 'citta' ? order : 'asc'}
                        onClick={() => handleSort('citta')}
                        sx={SORT_LABEL_SX}
                      >
                        Località
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ width: isCompactTable ? '20%' : '18%' }}>Contatti</TableCell>
                    <TableCell align="center" sx={{ width: '8%' }}>
                      <TableSortLabel
                        active={orderBy === 'numero_cantieri'}
                        direction={orderBy === 'numero_cantieri' ? order : 'asc'}
                        onClick={() => handleSort('numero_cantieri')}
                        sx={SORT_LABEL_SX}
                      >
                        Cantieri
                      </TableSortLabel>
                    </TableCell>
                    {!isCompactTable && (
                      <TableCell align="center" sx={{ width: '8%' }}>
                        <TableSortLabel
                          active={orderBy === 'numero_interventi'}
                          direction={orderBy === 'numero_interventi' ? order : 'asc'}
                          onClick={() => handleSort('numero_interventi')}
                          sx={SORT_LABEL_SX}
                        >
                          Interventi
                        </TableSortLabel>
                      </TableCell>
                    )}
                    <TableCell align="right" sx={{ width: isCompactTable ? '20%' : '16%' }}>
                      Azioni
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {clientiPaginati.map((cliente) => (
                    <TableRow
                      key={cliente.id}
                      hover
                      onClick={() => handleOpenDetailDialog(cliente)}
                      sx={{ cursor: 'pointer', '&:last-child td': { border: 0 } }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar
                            sx={{ width: 40, height: 40, bgcolor: '#1a1a1a', fontWeight: 700, fontSize: '0.8rem' }}
                          >
                            {iniziali(cliente.ragione_sociale)}
                          </Avatar>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={600}>
                              {cliente.ragione_sociale}
                            </Typography>
                            {/* sotto i 1200px la P.IVA non ha una colonna: la mostriamo qui */}
                            {isCompactTable && cliente.partita_iva && (
                              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                {cliente.partita_iva}
                              </Typography>
                            )}
                            {!isCompactTable && cliente.referente && (
                              <Typography variant="caption" color="text.secondary">
                                Ref: {cliente.referente}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>

                      {!isCompactTable && (
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {cliente.partita_iva || '—'}
                          </Typography>
                        </TableCell>
                      )}

                      <TableCell>
                        {/* città e provincia sulla STESSA riga: prima "(NO)" andava a capo
                            e faceva crescere l'altezza di ogni riga */}
                        <Typography variant="body2">
                          {cliente.citta || '—'}
                          {cliente.provincia && (
                            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                              ({cliente.provincia})
                            </Typography>
                          )}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        {cliente.telefono && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                            <PhoneIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                            <Typography variant="body2">{cliente.telefono}</Typography>
                          </Box>
                        )}
                        {cliente.email && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                            <EmailIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                            <Typography variant="body2" noWrap title={cliente.email} sx={{ maxWidth: { md: 160, lg: 220 } }}>
                              {cliente.email}
                            </Typography>
                          </Box>
                        )}
                        {!cliente.telefono && !cliente.email && (
                          <Typography variant="body2" color="text.secondary">—</Typography>
                        )}
                      </TableCell>

                      <TableCell align="center">
                        <Chip
                          label={cliente.numero_cantieri}
                          size="small"
                          sx={{
                            bgcolor: cliente.numero_cantieri > 0 ? '#dbeafe' : '#f3f4f6',
                            color: cliente.numero_cantieri > 0 ? '#1e40af' : '#6b7280',
                            fontWeight: 700,
                            minWidth: 32,
                          }}
                        />
                      </TableCell>

                      {!isCompactTable && (
                        <TableCell align="center">
                          <Chip
                            label={cliente.numero_interventi}
                            size="small"
                            sx={{
                              bgcolor: cliente.numero_interventi > 0 ? '#dcfce7' : '#f3f4f6',
                              color: cliente.numero_interventi > 0 ? '#166534' : '#6b7280',
                              fontWeight: 700,
                              minWidth: 32,
                            }}
                          />
                        </TableCell>
                      )}

                      <TableCell align="right" onClick={(e) => e.stopPropagation()} sx={{ whiteSpace: 'nowrap' }}>
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                          <Tooltip title="Apri scheda">
                            <IconButton
                              size="small"
                              aria-label={`Apri la scheda di ${cliente.ragione_sociale}`}
                              onClick={() => handleOpenDetailDialog(cliente)}
                              sx={{ color: '#3b82f6', '&:hover': { bgcolor: '#eff6ff' } }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Modifica">
                            <IconButton
                              size="small"
                              aria-label={`Modifica ${cliente.ragione_sociale}`}
                              onClick={() => handleOpenDialog(cliente)}
                              sx={{ color: '#6b7280', '&:hover': { bgcolor: '#f3f4f6' } }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Elimina">
                            <IconButton
                              size="small"
                              aria-label={`Elimina ${cliente.ragione_sociale}`}
                              onClick={() => handleOpenDeleteDialog(cliente)}
                              sx={{ color: '#dc2626', ml: 0.5, '&:hover': { bgcolor: '#fef2f2' } }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {!vuoto && (
            <TablePagination
              component="div"
              count={clientiFiltrati.length}
              page={paginaCorrente}
              onPageChange={(e, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={isPhone ? [] : [10, 25, 50, 100]}
              labelRowsPerPage="Per pagina:"
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} di ${count}`}
              sx={{
                borderTop: '1px solid',
                borderColor: 'divider',
                bgcolor: '#fafafa',
                // sotto i 400px la barra andava in overflow orizzontale
                '& .MuiTablePagination-toolbar': {
                  px: { xs: 1, sm: 2 },
                  flexWrap: 'wrap',
                  justifyContent: { xs: 'center', sm: 'flex-end' },
                },
                '& .MuiTablePagination-displayedRows, & .MuiTablePagination-selectLabel': {
                  fontSize: '0.875rem',
                  my: 0,
                },
              }}
            />
          )}
        </Paper>
      </Container>

      {/* FAB solo su telefono: "Nuovo Cliente" raggiungibile a qualsiasi scroll */}
      {isPhone && (
        <Fab
          color="primary"
          aria-label="Nuovo cliente"
          onClick={() => handleOpenDialog()}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 20,
            bgcolor: '#e50000',
            '&:hover': { bgcolor: '#c50000' },
            boxShadow: '0 6px 20px rgba(229,0,0,0.45)',
          }}
        >
          <AddIcon />
        </Fab>
      )}

      {/* ---------------------------------------------------------- dialog form */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        fullScreen={isPhone}
        TransitionComponent={Fade}
        PaperProps={{
          component: 'form',
          onSubmit: handleSubmit,
          noValidate: true,
          elevation: 0,
          sx: {
            borderRadius: isPhone ? 0 : 3,
            border: isPhone ? 'none' : '1px solid',
            borderColor: 'divider',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid',
            borderColor: 'divider',
            py: 2,
            px: { xs: 2, sm: 3 },
            // su telefono il form è lungo: intestazione e pulsanti restano visibili
            position: isPhone ? 'sticky' : 'static',
            top: 0,
            bgcolor: 'background.paper',
            zIndex: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: currentCliente ? '#f3f4f6' : '#fef2f2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {currentCliente ? <EditIcon sx={{ color: '#6b7280' }} /> : <AddIcon sx={{ color: '#dc2626' }} />}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" fontWeight={700} noWrap>
                {currentCliente ? 'Modifica Cliente' : 'Nuovo Cliente'}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                {currentCliente ? currentCliente.ragione_sociale : 'Inserisci i dati del nuovo cliente'}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={handleCloseDialog} size="small" aria-label="Chiudi" disabled={saving}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3, mt: 1, borderRadius: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mt: error ? 0 : 0.5 }}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
                DATI AZIENDA
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="ragione_sociale"
                label="Ragione Sociale"
                fullWidth
                required
                autoFocus={!isPhone}
                inputRef={primoCampoRef}
                value={formData.ragione_sociale}
                onChange={handleInputChange}
                error={Boolean(formErrors.ragione_sociale)}
                helperText={formErrors.ragione_sociale}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="partita_iva"
                label="Partita IVA"
                fullWidth
                value={formData.partita_iva}
                onChange={handleInputChange}
                error={Boolean(formErrors.partita_iva)}
                helperText={formErrors.partita_iva || '11 cifre'}
                inputProps={{ inputMode: 'numeric', maxLength: 11 }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="codice_fiscale"
                label="Codice Fiscale"
                fullWidth
                value={formData.codice_fiscale}
                onChange={handleInputChange}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="telefono"
                label="Telefono"
                fullWidth
                type="tel"
                value={formData.telefono}
                onChange={handleInputChange}
                inputProps={{ inputMode: 'tel' }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mt: 1 }}>
                INDIRIZZO
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                name="indirizzo"
                label="Indirizzo"
                fullWidth
                required
                value={formData.indirizzo}
                onChange={handleInputChange}
                error={Boolean(formErrors.indirizzo)}
                helperText={formErrors.indirizzo}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField
                name="citta"
                label="Città"
                fullWidth
                required
                value={formData.citta}
                onChange={handleInputChange}
                error={Boolean(formErrors.citta)}
                helperText={formErrors.citta}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={7} sm={4}>
              <TextField
                name="cap"
                label="CAP"
                fullWidth
                required
                value={formData.cap}
                onChange={handleInputChange}
                error={Boolean(formErrors.cap)}
                helperText={formErrors.cap}
                inputProps={{ inputMode: 'numeric', maxLength: 5 }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={5} sm={3}>
              <TextField
                name="provincia"
                label="Provincia"
                fullWidth
                required
                value={formData.provincia}
                onChange={handleInputChange}
                error={Boolean(formErrors.provincia)}
                helperText={formErrors.provincia}
                inputProps={{ maxLength: 2, style: { textTransform: 'uppercase' } }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mt: 1 }}>
                CONTATTI
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="email"
                label="Email"
                fullWidth
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                error={Boolean(formErrors.email)}
                helperText={formErrors.email}
                inputProps={{ inputMode: 'email', autoCapitalize: 'none' }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="pec"
                label="PEC"
                fullWidth
                type="email"
                value={formData.pec}
                onChange={handleInputChange}
                error={Boolean(formErrors.pec)}
                helperText={formErrors.pec}
                inputProps={{ inputMode: 'email', autoCapitalize: 'none' }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mt: 1 }}>
                REFERENTE
              </Typography>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                name="referente"
                label="Nome Referente"
                fullWidth
                value={formData.referente}
                onChange={handleInputChange}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                name="telefono_referente"
                label="Telefono Referente"
                fullWidth
                type="tel"
                value={formData.telefono_referente}
                onChange={handleInputChange}
                inputProps={{ inputMode: 'tel' }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                name="email_referente"
                label="Email Referente"
                fullWidth
                type="email"
                value={formData.email_referente}
                onChange={handleInputChange}
                error={Boolean(formErrors.email_referente)}
                helperText={formErrors.email_referente}
                inputProps={{ inputMode: 'email', autoCapitalize: 'none' }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                name="note"
                label="Note"
                fullWidth
                multiline
                rows={3}
                value={formData.note}
                onChange={handleInputChange}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            gap: 1,
            position: isPhone ? 'sticky' : 'static',
            bottom: 0,
            bgcolor: 'background.paper',
          }}
        >
          <Button
            onClick={handleCloseDialog}
            disabled={saving}
            sx={{ color: '#6b7280', px: 3, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
          >
            Annulla
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={saving}
            startIcon={
              saving ? (
                <CircularProgress size={18} color="inherit" />
              ) : currentCliente ? (
                <CheckCircleIcon />
              ) : (
                <AddIcon />
              )
            }
            sx={{
              bgcolor: '#e50000',
              px: 3,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { bgcolor: '#c50000' },
            }}
          >
            {saving ? 'Salvataggio…' : currentCliente ? 'Salva Modifiche' : 'Crea Cliente'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------------------------------------------------------- conferma eliminazione */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Fade}
        PaperProps={{
          elevation: 0,
          sx: { borderRadius: 3, border: '1px solid', borderColor: 'divider', m: { xs: 2, sm: 4 } },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                bgcolor: '#fef2f2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <WarningIcon sx={{ color: '#dc2626', fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                Conferma Eliminazione
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Questa azione non può essere annullata
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Sei sicuro di voler eliminare il cliente{' '}
            <strong>“{clienteToDelete?.ragione_sociale}”</strong>?
          </Typography>
          <Alert
            severity="warning"
            icon={<WarningIcon />}
            sx={{ borderRadius: 2, bgcolor: '#fffbeb', border: '1px solid #fde68a' }}
          >
            Se il cliente ha cantieri, interventi o rapportini collegati, ti verrà chiesto di
            confermare l'eliminazione a cascata (verranno eliminati anche quelli).
          </Alert>
          {deleteError && (
            <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }} onClose={() => setDeleteError('')}>
              {deleteError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            onClick={handleCloseDeleteDialog}
            disabled={deleting}
            sx={{ color: '#6b7280', px: 3, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
          >
            Annulla
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={18} color="inherit" /> : <DeleteIcon />}
            sx={{ px: 3, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
          >
            {deleting ? 'Eliminazione…' : 'Elimina Cliente'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------------------------------------------------------- eliminazione a cascata */}
      <Dialog
        open={blockDialogOpen}
        onClose={handleCloseBlockDialog}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Fade}
        PaperProps={{
          elevation: 0,
          sx: { borderRadius: 3, border: '1px solid', borderColor: 'divider', m: { xs: 2, sm: 4 } },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                bgcolor: '#fffbeb',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <WarningIcon sx={{ color: '#dc2626', fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                Confermi l'eliminazione?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Verranno eliminati anche gli elementi collegati
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert
            severity="warning"
            icon={<WarningAmberIcon />}
            sx={{ borderRadius: 2, mb: 2, bgcolor: '#fffbeb', border: '1px solid #fde68a' }}
          >
            <AlertTitle sx={{ fontWeight: 700 }}>
              Stai per eliminare il cliente
              {blockedCliente?.ragione_sociale ? ` “${blockedCliente.ragione_sociale}”` : ''}
            </AlertTitle>
            Verranno eliminati DEFINITIVAMENTE anche tutti gli elementi collegati elencati qui
            sotto. L'operazione non è reversibile.
          </Alert>

          <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mb: 1 }}>
            ELEMENTI COLLEGATI
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            <Chip
              icon={<BuildIcon sx={{ fontSize: 18 }} />}
              label={`${blockInfo?.cantieri ?? 0} ${(blockInfo?.cantieri ?? 0) === 1 ? 'Cantiere' : 'Cantieri'}`}
              sx={{ bgcolor: '#dbeafe', color: '#1e40af', fontWeight: 700, '& .MuiChip-icon': { color: '#1e40af' } }}
            />
            <Chip
              icon={<TrendingUpIcon sx={{ fontSize: 18 }} />}
              label={`${blockInfo?.interventi ?? 0} ${(blockInfo?.interventi ?? 0) === 1 ? 'Intervento' : 'Interventi'}`}
              sx={{ bgcolor: '#dcfce7', color: '#166534', fontWeight: 700, '& .MuiChip-icon': { color: '#166534' } }}
            />
            <Chip
              icon={<DescriptionIcon sx={{ fontSize: 18 }} />}
              label={`${blockInfo?.rapportini ?? 0} ${(blockInfo?.rapportini ?? 0) === 1 ? 'Rapportino' : 'Rapportini'}`}
              sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 700, '& .MuiChip-icon': { color: '#92400e' } }}
            />
          </Box>

          {blockInfo?.cantieri_nomi?.length > 0 && (
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden', mb: 1 }}>
              <Box sx={{ px: 2, py: 1, bgcolor: '#fafafa', borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="caption" fontWeight={700} color="text.secondary">
                  CANTIERI COLLEGATI
                  {blockInfo.cantieri > blockInfo.cantieri_nomi.length &&
                    ` (primi ${blockInfo.cantieri_nomi.length} di ${blockInfo.cantieri})`}
                </Typography>
              </Box>
              <List dense disablePadding sx={{ maxHeight: 200, overflowY: 'auto' }}>
                {blockInfo.cantieri_nomi.map((nome, index) => (
                  <React.Fragment key={`${nome}-${index}`}>
                    {index > 0 && <Divider component="li" />}
                    <ListItem>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <BuildIcon sx={{ fontSize: 20, color: '#3b82f6' }} />
                      </ListItemIcon>
                      <ListItemText primary={nome} primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }} />
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            </Box>
          )}

          <Alert severity="info" sx={{ borderRadius: 2, mt: 2 }}>
            Confermando, il cliente e tutti gli elementi sopra (incluse timbrature, presenze e
            firme) verranno eliminati. Procedi solo se sei sicuro.
          </Alert>

          {blockError && (
            <Alert severity="error" sx={{ borderRadius: 2, mt: 2 }} onClose={() => setBlockError('')}>
              {blockError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            onClick={handleCloseBlockDialog}
            disabled={deleting}
            sx={{ color: '#6b7280', px: 3, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
          >
            Annulla
          </Button>
          <Button
            onClick={handleCascadeDelete}
            variant="contained"
            color="error"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={18} color="inherit" /> : <DeleteIcon />}
            sx={{ px: 3, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
          >
            {deleting ? 'Eliminazione…' : 'Elimina tutto'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* conferma delle azioni: prima non c'era alcun riscontro dopo crea/modifica/elimina */}
      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: isPhone ? 'center' : 'right' }}
        sx={{ mb: isPhone ? 10 : 0 }}
      >
        <Alert
          onClose={() => setToast(null)}
          severity={toast?.severity || 'success'}
          variant="filled"
          sx={{ width: '100%', borderRadius: 2 }}
        >
          {toast?.messaggio}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Clienti;
